import { cloneDeep, get, has, set, memoize } from 'lodash';
import moment from 'moment';
import {
  BACKEND_DATE_FORMAT,
  MINIMUM_DATE,
  INPUT_DATE_FORMAT,
  MINIMUM_ADULT_AGE,
} from '../../../config/helpers';
import {
  calculatePriceOnSelectedPaxType,
  calculateTotalPricePerCurrency,
  getCurrencyPriceFromCurrencyOptions,
  getPricePerPaxTypePerCurrency,
} from '../../../util/currency';
import { isStopoverOfType, StopoverTypes } from '../BookingStopoverOptions/helpers';

export const tourBookingStep = {
  EVENT_OPTIONS: 'eventOptions',
  STOPOVER_OPTIONS: 'stopoverOptions',
  TRAVEL_OPTIONS: 'travelOptions',
  PASSENGER_DETAILS: 'passengerDetails',
  SUMMARY: 'summary',
  PAYMENT: 'payment',
  PROCESS_PAYMENT: 'processPayment',
  ACKNOWLEDGEMENT: 'acknowledgement',
};

export const accomTypes = {
  MULTI: 'multi',
  HOTEL: 'hotel',
  MOTORHOME: 'motorhome',
};

export const roomTypeCodes = {
  SINGLE_ROOM: 'SG',
  DOUBLE_ROOM: 'DB',
  TWIN_ROOM: 'TW',
  TRIPLE_ROOM: 'TR',
  DOUBLE_REQUESTED: 'TD',
};

export const getOption = (options, optionKey) => {
  const { defaultOption = {}, upgrades = [] } = options || {};
  const allOptions = [defaultOption, ...upgrades];
  return allOptions.find((option) => option.key === optionKey);
};

export const getPassengers = (paxSelection, paxTypes) => {
  if (!paxSelection || !paxTypes) return '';

  const passengerCounts = Object.entries(paxSelection)
    .map(([key, count]) => {
      const paxType = paxTypes.find((type) => type.key === key);
      return paxType && count > 0 ? `${count} ${paxType.name}` : null;
    })
    .filter(Boolean);

  return passengerCounts.join(', ');
};

export const formatDate = (date) => {
  const dateOfBirth = moment(date, BACKEND_DATE_FORMAT);

  return moment(MINIMUM_DATE).isAfter(dateOfBirth) ? '' : dateOfBirth.format(INPUT_DATE_FORMAT);
};

export const preventKeyCode = (keyCode) =>
  function onKeyPress(event) {
    const pressedKeyCode = event.keyCode || event.which;
    if (pressedKeyCode === keyCode) {
      event.preventDefault();
    }
  };

export const markRequiredFields = (fieldsetProps, requiredFieldNames) => {
  requiredFieldNames.forEach((requiredFieldName) => {
    if (!has(fieldsetProps, requiredFieldName)) return;

    set(fieldsetProps, `${requiredFieldName}.isRequired`, true);
  });
};

export const initFieldsetProps = (fieldset) =>
  Object.fromEntries(
    Object.entries(fieldset).map(([key, val]) => [
      key,
      Object(val) === val && (key === 'address' || key === 'fullName')
        ? initFieldsetProps(val)
        : {
            isInvalid: false,
            isRequired: false,
            regex: undefined,
            value:
              val ??
              (key === 'roomAllocation'
                ? {
                    key: null,
                    index: -1,
                  }
                : ''),
          },
    ])
  );

export const validateFieldset = (fieldset, callback) => {
  let fieldsetIsInvalid = false;

  Object.keys(fieldset).forEach((fieldKey) => {
    const field = fieldset[fieldKey];
    let childIsInvalid = false;

    if (Object(field) === field && (fieldKey === 'address' || fieldKey === 'fullName')) {
      childIsInvalid = validateFieldset(field, callback);
    } else {
      if (Object(field) !== field || !('value' in field)) return;

      field.isInvalid = false;

      const fieldHasValue = field.value !== '' && field.value !== null;
      if (field.isRequired && !fieldHasValue) field.isInvalid = true;

      // ensure fields don't have white space.
      if (field.isRequired && field.value.trim && field.value.trim() !== field.value)
        field.isInvalid = true;

      if (field.regex && fieldHasValue) {
        const regexIsValid = field.regex.test(field.value);
        if (!regexIsValid) field.isInvalid = true;
      }

      if (callback) {
        const params = { fieldKey, field };
        field.isInvalid = callback(params);
      }

      if (Object(field) === field && fieldKey === 'amount') {
        const amountValue = parseFloat(field.value);
        if (Number.isNaN(amountValue) || amountValue <= 1) {
          field.isInvalid = true;
        }
      }

      const isNameField = fieldKey === 'forename' || fieldKey === 'surname';
      const isValidName =
        typeof field.value === 'string' && field.value.length >= 2 && field.value.length <= 20;
      if (isNameField && !isValidName) {
        field.isInvalid = true;
      }
    }

    if (field.isInvalid || childIsInvalid) fieldsetIsInvalid = true;
  });

  return fieldsetIsInvalid;
};

export const updateField = (state, name, value, callback) => {
  let newValue = value;
  const stateClone = cloneDeep(state);
  const currentValue = get(stateClone, name);

  if (currentValue === undefined) return {};

  if (callback) {
    const params = { name, value, currentValue };
    newValue = callback(params);
  }

  if (newValue === 'true') newValue = true;
  else if (newValue === 'false') newValue = false;

  const isFieldObject = Object(currentValue) === currentValue && 'value' in currentValue;

  set(stateClone, `${name}${isFieldObject ? '.value' : ''}`, newValue);

  return stateClone;
};

export function getOptionUpgradesInGroup(groupKey, optionGroups) {
  const {
    defaultOption = {},
    upgrades = [],
    options = [],
  } = optionGroups?.find((og) => og.key === groupKey) || {};
  return [defaultOption, ...upgrades, ...options];
}
/**
 * Retrieve a specific option from a set of potential choices within a booking system based on provided keys.
 * This function is crucial for dynamically fetching details like room types or tour options based on user selections.
 * @param {string} groupKey - Identifies a group of options (e.g., a hotel's ID).
 * @param {string} optionKey - Specifies the unique identifier of the desired option within the group (e.g., a specific room type).
 * @param {array} optionGroups - Contains arrays of all option groups, where each group is related to different booking aspects.
 * @returns {object|null} - Returns the found option if it exists; otherwise, returns null and logs an error.
 */
export function getOptionUpgrade(groupKey, optionKey, optionGroups) {
  // Gather all options available under the specified groupKey from the array of optionGroups.
  // For instance, if groupKey is 'hotel123', allOptions might contain room types like 'Standard', 'Deluxe', 'Suite'.
  const allOptions = getOptionUpgradesInGroup(groupKey, optionGroups);

  // Search through the gathered options to find the one that matches the specified optionKey.
  // For example, optionKey might be 'Deluxe', and this line finds the 'Deluxe' room type within 'hotel123's options.
  const foundOption = allOptions.find((option) => option.key === optionKey);

  return foundOption;
}

export function calculateSingleSupplementCost(selectedOptions, packageData, currencyPriceSetting) {
  function getSingleSupplementCount(selectedHotel, hotelOption) {
    let singleSupplementCount = 0;

    if (!selectedHotel.rooms) return singleSupplementCount;

    selectedHotel.rooms.forEach((room) => {
      const { roomKey, roomCount } = room;
      const { roomTypes = [] } =
        getOptionUpgrade(selectedHotel.groupKey, selectedHotel.optionKey, [hotelOption]) || {};
      const { code } = roomTypes.find((r) => r.key === roomKey) || {};
      if (code === roomTypeCodes.SINGLE_ROOM) {
        singleSupplementCount += roomCount;
      }
    });

    return singleSupplementCount;
  }

  let singleSupplementCost = 0;

  const selectedHotels = selectedOptions.accommodations?.filter(
    (a) => a.accommodationType === accomTypes.HOTEL
  );
  if (!selectedHotels?.length && !selectedOptions.stopovers.length) return singleSupplementCost;

  const hasHotels = selectedHotels?.length > 0;
  const selectedHotelToUse = hasHotels ? selectedHotels[0] : selectedOptions.stopovers[0].hotel;

  const hotelOptions = packageData.accommodationOptions?.filter(
    (ao) => ao.accommodationType === accomTypes.HOTEL
  );

  if (hasHotels) {
    selectedHotels.forEach((selectedHotel) => {
      const foundHotel =
        getOptionUpgrade(selectedHotel.groupKey, selectedHotel.optionKey, hotelOptions) || {};

      const singleSupplementCount = getSingleSupplementCount(selectedHotel, hotelOptions[0]);
      const price = getCurrencyPriceFromCurrencyOptions(
        foundHotel.option?.costsPerSingleSupplement || [],
        currencyPriceSetting.currency
      );

      singleSupplementCost += price * singleSupplementCount;
    });
  }

  if (selectedOptions.stopovers.length > 0) {
    selectedOptions.stopovers.forEach((selectedStopover) => {
      const foundStopover = getOptionUpgrade(
        selectedStopover.groupKey,
        selectedStopover.optionKey,
        packageData.stopoverOptions
      );

      if (foundStopover) {
        const hotelOptionToUse = hasHotels ? hotelOptions[0] : foundStopover.option.hotelOption;
        const foundHotel =
          getOptionUpgrade(selectedStopover.hotel?.groupKey, selectedStopover.hotel?.optionKey, [
            foundStopover.option.hotelOption,
          ]) || {};

        const singleSupplementCount = getSingleSupplementCount(
          selectedStopover.hotel,
          hotelOptionToUse
        );
        const price = getCurrencyPriceFromCurrencyOptions(
          foundHotel.option?.costsPerSingleSupplement || [],
          currencyPriceSetting.currency
        );

        singleSupplementCost += price * singleSupplementCount;
      }
    });
  }

  return singleSupplementCost;
}

/**
 * Gets the total base price across all passenger types for a specific currency
 * @param {Object} packageData - Package data containing pricePerPax information
 * @param {string} currencyCode - The currency code to get the price for
 * @param {Object} selectedPaxKey - Object containing counts for each passenger type
 * @returns {number} The total price for the specified currency
 */
export const getBasePriceFromPerPaxTypePerCurrency = (
  packageData,
  currencyCode,
  selectedPaxKey = {}
) => {
  if (!packageData?.pricePerPax) return 0;

  return packageData.pricePerPax.reduce((total, paxType) => {
    const paxCount = selectedPaxKey[paxType.paxKey] || 0;
    const currencyPrice = paxType.pricePerCurrency?.find(
      (price) => price.currency === currencyCode
    );
    return total + (currencyPrice?.price || 0) * paxCount;
  }, 0);
};

const calculateAccommodationCost = (accommodations, packageData, selectedPaxKey, currency) => {
  if (!accommodations?.length) return 0;

  return accommodations.reduce((total, selectedAccom) => {
    const foundAccom = getOptionUpgrade(
      selectedAccom.groupKey,
      selectedAccom.optionKey,
      packageData.accommodationOptions
    );

    if (!foundAccom) return total;

    let price = getPricePerPaxTypePerCurrency(
      calculatePriceOnSelectedPaxType(foundAccom, selectedPaxKey),
      currency,
      selectedPaxKey
    );

    if (foundAccom?.accommodationType === accomTypes.MOTORHOME) {
      price = getCurrencyPriceFromCurrencyOptions(foundAccom.totalPricePerCurrency, currency);
    }

    return total + price;
  }, 0);
};

const calculateTicketCost = (tickets, packageData, selectedPaxKey, currency) => {
  if (!tickets?.length) return 0;

  return tickets.reduce((total, selectedTicket) => {
    const foundTicket = getOptionUpgrade(
      selectedTicket.groupKey,
      selectedTicket.optionKey,
      packageData.ticketOptions
    );

    if (!foundTicket) return total;

    const price = getPricePerPaxTypePerCurrency(
      calculatePriceOnSelectedPaxType(foundTicket, selectedPaxKey),
      currency,
      selectedPaxKey
    );

    return total + price;
  }, 0);
};

const calculateExcursionCost = (excursions, packageData, selectedPaxKey, currency) => {
  if (!excursions?.length) return 0;

  return excursions.reduce((total, selectedExcursion) => {
    const foundExcursion = getOptionUpgrade(
      selectedExcursion.groupKey,
      selectedExcursion.optionKey,
      packageData.excursionOptions
    );

    if (!foundExcursion) return total;

    const price = getPricePerPaxTypePerCurrency(
      calculatePriceOnSelectedPaxType(foundExcursion, selectedPaxKey),
      currency,
      selectedPaxKey
    );

    return total + price;
  }, 0);
};

const calculateTransportCost = (transports, packageData, selectedPaxKey, currency) => {
  if (!transports?.length) return 0;

  return transports.reduce((total, selectedTransport) => {
    const allTransportCards = getOptionUpgradesInGroup(
      selectedTransport.groupKey,
      packageData.transportOptions
    );

    const selectedCard = allTransportCards.find((option) =>
      option.options.find((o) => o.key === selectedTransport.optionKey)
    );

    if (!selectedCard) return total;

    const selectedDepartureOptionInCard = selectedCard?.options?.find(
      (o) => o.key === selectedTransport.optionKey
    );

    const price = getPricePerPaxTypePerCurrency(
      calculatePriceOnSelectedPaxType(selectedDepartureOptionInCard, selectedPaxKey),
      currency,
      selectedPaxKey
    );

    return total + price;
  }, 0);
};

const calculateFlightCost = (flights, packageData, selectedPaxKey, currency, stopovers) => {
  if (
    !flights?.length ||
    stopovers?.find((stopover) => isStopoverOfType(stopover, StopoverTypes.PRE_EVENT))
  ) {
    return 0;
  }

  return flights.reduce((total, selectedFlight) => {
    if (!selectedFlight || Object.entries(selectedFlight).length === 0) return total;

    const foundFlight = getOptionUpgrade(
      selectedFlight.groupKey,
      selectedFlight.optionKey,
      packageData.flightOptions
    );

    if (!foundFlight) return total;

    const foundFlightClass = {
      pricePerPax:
        foundFlight.flightClasses.find(
          (flightClass) => flightClass.key === selectedFlight.flightClassKey
        ).pricePerPax || [],
    };

    const flightTotal = getPricePerPaxTypePerCurrency(
      calculatePriceOnSelectedPaxType(foundFlightClass, selectedPaxKey),
      currency,
      selectedPaxKey
    );

    return total + flightTotal;
  }, 0);
};

// Utility function to calculate prices for a specific passenger type
const calculatePricePerPassenger = (pricePerPax, selectedPaxKey, currency) => {
  if (!pricePerPax?.length || !selectedPaxKey) return 0;

  return Object.keys(selectedPaxKey).reduce((total, key) => {
    const matchingPax = pricePerPax.find((pax) => pax.paxKey === key);
    if (!matchingPax) return total;

    const currencyPrice = matchingPax.pricePerCurrency.find((price) => price.currency === currency);
    return total + (currencyPrice ? currencyPrice.price * selectedPaxKey[key] : 0);
  }, 0);
};

const calculateStopoverCost = (stopovers, packageData, selectedPaxKey, currency) => {
  if (!stopovers?.length) return 0;

  return stopovers.reduce((total, selectedStopover) => {
    const foundStopover = getOptionUpgrade(
      selectedStopover.groupKey,
      selectedStopover.optionKey,
      packageData.stopoverOptions
    );
    if (!foundStopover) return total;

    const { hotel, excursion, flights = [] } = selectedStopover;
    const foundStopoverData = foundStopover.data.find(
      (data) => data.key === selectedStopover.dataKey
    );
    if (!foundStopoverData) return total;

    // Calculate base stopover price
    let stopoverTotal = calculatePricePerPassenger(
      foundStopoverData.pricePerPax,
      selectedPaxKey,
      currency
    );

    // Add hotel cost
    const foundHotel = getOptionUpgrade(hotel.groupKey, hotel.optionKey, [
      foundStopover.option.hotelOption,
    ]);
    if (foundHotel) {
      stopoverTotal += calculatePricePerPassenger(foundHotel.pricePerPax, selectedPaxKey, currency);
    }

    // Add flights cost
    stopoverTotal += flights.reduce((flightTotal, selectedFlight) => {
      if (!selectedFlight?.optionKey) return flightTotal;

      const foundFlight = getOptionUpgrade(
        selectedFlight.groupKey,
        selectedFlight.optionKey,
        foundStopoverData.flightOptions
      );
      if (!foundFlight) return flightTotal;

      const foundFlightClass = foundFlight.flightClasses.find(
        (flightClass) => flightClass.key === selectedFlight.flightClassKey
      );
      if (!foundFlightClass) return flightTotal;

      return (
        flightTotal +
        calculatePricePerPassenger(foundFlightClass.pricePerPax, selectedPaxKey, currency)
      );
    }, 0);

    // Add excursion cost
    if (excursion) {
      const foundExcursion = getOptionUpgrade(excursion.groupKey, excursion.optionKey, [
        foundStopover.option.excursionOption,
      ]);
      if (foundExcursion) {
        stopoverTotal += calculatePricePerPassenger(
          foundExcursion.pricePerPax,
          selectedPaxKey,
          currency
        );
      }
    }

    return total + stopoverTotal;
  }, 0);
};

export const getTotalPrice = memoize(
  (state, packageData, currencyPriceSetting) => {
    // Destructure to make it easier to see what's coming in
    const { paxSelection, accommodations, tickets, excursions, transports, flights, stopovers } =
      state;

    const { currency } = currencyPriceSetting ?? {};

    // Each step in its own const for better debugging
    const basePrice = getBasePriceFromPerPaxTypePerCurrency(packageData, currency, paxSelection);
    const accommodationCost = calculateAccommodationCost(
      accommodations,
      packageData,
      paxSelection,
      currency
    );
    const ticketCost = calculateTicketCost(tickets, packageData, paxSelection, currency);
    const excursionCost = calculateExcursionCost(excursions, packageData, paxSelection, currency);
    const transportCost = calculateTransportCost(transports, packageData, paxSelection, currency);
    const flightCost = calculateFlightCost(flights, packageData, paxSelection, currency, stopovers);
    const stopoverCost = calculateStopoverCost(stopovers, packageData, paxSelection, currency);
    const singleSupplementCost = calculateSingleSupplementCost(
      state,
      packageData,
      currencyPriceSetting
    );

    return (
      basePrice +
      accommodationCost +
      ticketCost +
      excursionCost +
      transportCost +
      flightCost +
      stopoverCost +
      singleSupplementCost
    );
  },
  // Custom resolver for memoization cache key
  (state, packageData, currencyPriceSetting) =>
    JSON.stringify({
      paxSelection: state.paxSelection,
      accommodations: state.accommodations,
      tickets: state.tickets,
      excursions: state.excursions,
      transports: state.transports,
      flights: state.flights,
      stopovers: state.stopovers,
      currency: currencyPriceSetting?.currency,
    })
);

export const validateAgeForPaxType = (
  dateOfBirth,
  paxTypes,
  paxKey,
  validationPaxDateOfBirthInvalid,
  isLeadPassenger = false,
  isBookerAMember = false,
  siteMinimumBookingAge = MINIMUM_ADULT_AGE
) => {
  const paxTypeInfo = paxTypes.find((pt) => pt.key === paxKey.value);
  if (!paxTypeInfo || !dateOfBirth) return { isValid: true };

  const birthDate = moment(dateOfBirth, 'DDMMYYYY');
  if (!birthDate.isValid()) return { isValid: false, message: 'Invalid date format' };

  // Calculate age at time of booking
  const futureDate = moment();
  const age = futureDate.diff(birthDate, 'years');

  let isValid;
  if (isLeadPassenger) {
    if (isBookerAMember) {
      isValid = age >= paxTypeInfo.minimumAge;
    } else {
      // For non-member lead passengers, use siteMinimumBookingAge
      isValid = age >= siteMinimumBookingAge;
    }
  } else {
    isValid = age >= paxTypeInfo.minimumAge && age <= paxTypeInfo.maximumAge;
  }

  return {
    isValid,
    message: isValid ? '' : validationPaxDateOfBirthInvalid.replace('[paxType]', paxTypeInfo.name),
  };
};
